import React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { PermitApplicationStep } from "../../PermitApplicationContext";
import { PERMIT_APPLICATION_STEPS, getStepIndex } from "../../constants";
import type { Theme } from "@mui/material/styles";

export interface DesktopStepperProps {
  currentStep: PermitApplicationStep;
  isStepComplete: (step: PermitApplicationStep) => boolean;
  canProceedToStep: (step: PermitApplicationStep) => boolean;
  onStepClick: (step: PermitApplicationStep) => void;
}

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 19, // Center with 40px icon
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.action.disabled,
    borderRadius: 1,
  },
}));

interface CustomStepIconProps extends StepIconProps {
  stepIndex: number;
}

const CustomStepIcon: React.FC<CustomStepIconProps> = ({ active, completed, stepIndex }) => {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: "1rem",
        border: 2,
        borderColor: completed || active ? "primary.main" : "action.disabled",
        backgroundColor: completed ? "primary.main" : "background.paper",
        color: completed ? "primary.contrastText" : active ? "primary.main" : "text.disabled",
        transition: (theme: Theme) =>
          theme.transitions.create(["background-color", "border-color", "color"], {
            duration: theme.transitions.duration.short,
          }),
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 20 }} /> : stepIndex + 1}
    </Box>
  );
};

export const DesktopStepper: React.FC<DesktopStepperProps> = ({
  currentStep,
  isStepComplete,
  canProceedToStep,
  onStepClick,
}) => {
  const activeStepIndex = getStepIndex(currentStep);

  const handleStepClick = (step: PermitApplicationStep) => {
    if (canProceedToStep(step) && step !== currentStep) {
      onStepClick(step);
    }
  };

  return (
    <Stepper activeStep={activeStepIndex} alternativeLabel connector={<CustomConnector />}>
      {PERMIT_APPLICATION_STEPS.map(({ step, label }, index) => {
        const completed = isStepComplete(step);
        const canNavigate = canProceedToStep(step);
        const isCurrent = step === currentStep;

        return (
          <Step key={step} completed={completed}>
            <StepLabel
              StepIconComponent={(props: StepIconProps) => <CustomStepIcon {...props} stepIndex={index} />}
              onClick={() => handleStepClick(step)}
              sx={{
                cursor: canNavigate && !isCurrent ? "pointer" : "default",
                "& .MuiStepLabel-label": {
                  mt: 1,
                  fontWeight: isCurrent ? 500 : 400,
                  color: isCurrent || completed ? "text.primary" : "text.secondary",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default DesktopStepper;
